import { clearSession } from "../../utils/clearSession";
import axios from "axios";
import { apiEndPoint } from "../../statics/config";
import localizationStrings from "../../statics/localization";
import { readCache } from "../../utils/readCache";
import {
  JSON_CONTENT_TYPE_UTF8,
  decodeUtf8,
  parseResponseBody
} from "../../utils/charset";

// Production uses an HTTPS deployment's same-origin /api proxy unless explicitly
// configured. The legacy development endpoint is retained for local development.
export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development" ? "/api" : "/api")).replace(/\/$/, "");

/**
 * The backend labels every /client/* response `charset=ISO-8859-1` while
 * emitting UTF-8 bytes, and axios 0.21.4 posts bodies as a bare
 * `application/json` with no charset at all. Both leave Turkish letters
 * (ç ğ ı İ ö ş ü) to be mangled -- a saved "Çağrı" comes back as "Ã‡aÄŸrÄ±"
 * and, once re-saved, is corrupted for good.
 *
 * Pin both legs to UTF-8: ask for the response as raw bytes and decode them
 * ourselves, and always declare the charset we send.
 */
const UTF8_HANDLED = "__utf8TransportHandled";

const isPlainObjectBody = (data) =>
  data !== null &&
  typeof data === "object" &&
  !(typeof FormData !== "undefined" && data instanceof FormData) &&
  !(typeof Blob !== "undefined" && data instanceof Blob) &&
  !(typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(data)) &&
  !(typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams);

const decodeResponse = (response) => {
  const { method = "get", url = "" } = response?.config || {};
  if (method.toLowerCase() !== "get" && /\/client\/(interact|profiles|auth|login|logout)(\/|\?|$)/.test(url)) {
    readCache.clear();
  }
  if (response && response.config && response.config[UTF8_HANDLED]) {
    response.data = parseResponseBody(decodeUtf8(response.data));
  }
  return response;
};

function applyUtf8Transport(client) {
  client.interceptors.request.use((config) => {
    // Leave callers that asked for a specific responseType (blobs, streams)
    // alone; everything else is text we want to read as UTF-8.
    if (!config.responseType) {
      config.responseType = "arraybuffer";
      config[UTF8_HANDLED] = true;
    }

    const headers = config.headers || (config.headers = {});
    const declared = headers["Content-Type"] || headers["content-type"];

    if (declared) {
      // Respect an explicitly chosen type, just make sure it names a charset.
      if (!/charset=/i.test(declared)) {
        headers["Content-Type"] = `${declared};charset=UTF-8`;
      }
    } else if (isPlainObjectBody(config.data)) {
      // Matches what axios is about to serialize, minus the missing charset.
      headers["Content-Type"] = JSON_CONTENT_TYPE_UTF8;
    }

    return config;
  });

  // Registered before the 401 handlers below so those still see parsed data.
  client.interceptors.response.use(decodeResponse, (error) => {
    if (error && error.response) decodeResponse(error.response);
    return Promise.reject(error);
  });
}

applyUtf8Transport(axios);

// Add a request interceptor
axios.interceptors.request.use(
  function (config) {
    if (!config.url.startsWith("http")) {
      config.url = API_BASE_URL + config.url;
    }

    if (config.url.startsWith(API_BASE_URL)) {
      // NPVR and profile-bound endpoints rely on cookie context (user-info).
      config.withCredentials = true;
    }

    if (!config.timeout) config.timeout = 20000;
    if (config.url.startsWith(API_BASE_URL) && config.headers.Authorization === undefined) config.headers.Authorization = localStorage.getItem("userToken");

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// MW returns a terminated session in an envelope, sometimes with HTTP 200.
const isMwSessionEnded = data => data?.IsSucceeded === false &&
  Number(data.ResultCode) === 403 && data.Message === "key_translation_error_code_403";
const handleSessionResponse = response => {
  if (!isMwSessionEnded(response.data)) {
    if (response.data?.IsSucceeded === false && /\/client\//.test(response.config?.url || "")) {
      const error = new Error(response.data.Message || "Request failed");
      error.code = response.data.ResultCode;
      error.response = response;
      return Promise.reject(error);
    }
    return response;
  }
  forceLogout();
  const error = new Error("Oturumunuz sona erdi.");
  error.code = 403;
  error.response = response;
  return Promise.reject(error);
};

// Both clients use the same session-expiration contract; login validation
// and transient network failures must not invalidate a valid session.
const handleSessionError = (error) => {
  const message = String(error?.response?.data?.message || error?.response?.data?.Message || "").toLowerCase();
  if (isMwSessionEnded(error?.response?.data) || (error?.response?.status === 401 && (
    message.includes("quota full") ||
    (message.includes("jwt") && message.includes("expired")) ||
    (message.includes("session") && message.includes("terminated")) ||
    message.includes("oturumunuz sona erdi")
  ))) forceLogout();
  return Promise.reject(error);
};
axios.interceptors.response.use(handleSessionResponse, handleSessionError);

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  withCredentials: true
});

// Instances do not inherit interceptors, so wire UTF-8 handling up separately.
applyUtf8Transport(axiosInstance);

axiosInstance.interceptors.request.use(
  (config) => {
    if (config.skipAuth) delete config.headers.Authorization;
    else if (config.headers.Authorization === undefined) config.headers.Authorization = localStorage.getItem("userToken");
    return config;
  },
  (error) => Promise.reject(error)
);

let isLoggingOut = false;

function forceLogout() {
  if (isLoggingOut) return;
  isLoggingOut = true;

  clearSession();
  window.location.replace(`${window.location.pathname || "/"}#/login`);
}

axiosInstance.interceptors.response.use(handleSessionResponse, handleSessionError);

const getFrameConfigs = async (userUrl, sessionId) => {
  return await axios.get(`${userUrl}/sdk_getuserframeconfig.jsp`);
};

const setCookieApi = async (key, value) => {
  let apiEndpoint = localStorage.getItem("interfaceurl");
  let sessiondId = localStorage.getItem("sessionid");
  return await axiosInstance.post(
    `${apiEndPoint}/sdk_setcookie.jsp`,
    `key=${key}&value=${value}`
  );
};

const getCookieApi = async () => {
  let apiEndpoint = localStorage.getItem("interfaceurl");
  let sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getcookie.jsp`);
};

const setCookieApiCall = async (key, value, isRefresh = true) => {
  const { data } = await setCookieApi(key, value);
  if (isRefresh) {
    localizationStrings.setLanguage(value);
    window.location.reload();
  }
};

const getColumnList = async (columnCode, orderType) => {
  let apiEndpoint = localStorage.getItem("interfaceurl");
  return await axiosInstance.get(`${apiEndPoint}/sdk_getcolumnlist.jsp`, {
    params: {
      columncode: columnCode,
      ordertype: orderType
    }
  });
};

const getWebSocketInfo = async (userCode, mesReportUrl, deviceid) => {
  return await axios.get(`${mesReportUrl}/terminalimporthttpsreq`, {
    params: {
      TransactionID: new Date().getTime(),
      UserID: userCode,
      TerminalFlag: 4,
      DeviceID: deviceid,
      Timestamp: new Date().getTime()
    }
  });
};

const getEpgDate = async () => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios.get(`${apiEndpoint}/sdk_getepgdate.jsp`);
};

const getUserLanguagePreferences = async () => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  let obj = `{"lng":"||"}`;
  return await axios.get(`${apiEndpoint}/sdk_getuserdata.jsp`, {
    params: {
      datatype: 20
      // datavalue: encodeURIComponent(obj)
    }
  });
};

const setUserLanguagePreferences = async (obj) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  // let obj = `{"lng":"||"}`;
  return await axiosInstance.get(`${apiEndPoint}/sdk_setuserdata.jsp`, {
    params: {
      datatype: 20,
      datavalue: obj
    }
  });
};

const getUserLastChannelData = async () => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  var sessiondId = localStorage.getItem("sessionid");
  return await axios
    .get(`${apiEndpoint}/sdk_getuserdata.jsp`, {
      params: {
        datatype: 3
      },
      timeout: 1000
    })
    .catch((err) => {
      return err;
    });
};

const getUserOnlineQueryApi = async (url, usercode) => {
  var sessiondId = localStorage.getItem("sessionid");
  // let obj = `{"lng":"||"}`;
  return await axios.post(
    `${url}/useronlinequery`,
    `{ "usercode": "${usercode}" }`
  );
};

const getEncryptData = async (isNeedTime) => {
  var apiEndpoint = localStorage.getItem("interfaceurl");
  return await axios.get(`${apiEndpoint}/sdk_getencryptdata.jsp`, {
    params: {
      isneedtimestamp: isNeedTime
    }
  });
};

export {
  axiosInstance,
  getFrameConfigs,
  getColumnList,
  getWebSocketInfo,
  getEpgDate,
  getUserLanguagePreferences,
  setUserLanguagePreferences,
  setCookieApi,
  setCookieApiCall,
  getCookieApi,
  getUserOnlineQueryApi,
  getEncryptData,
  getUserLastChannelData
};
